import React, { useState, useCallback } from "react";
import { Grid } from "@mui/material";
import PropTypes from "prop-types";
import PartnerExtraInformationComponent from "./PartnerExtraInformationComponent";
import { UPDATE_PARTNER_EXTRA_INFO } from "./mutations";
import { PARTNER_EXTRA_INFO_QUERY } from "./queries";
import { withTranslation } from "react-i18next";
import { useQueryWithSimpleErrorHandling } from "hooks/apolloWrappers";
import { useMutation } from "@apollo/client";
import { withRights } from "providers/RightsConsumer";
import { useTranslation } from "react-i18next";
import { testPhone } from "utils/pure-validation/primitiveValidators";
import { useSelector, shallowEqual } from "react-redux";
import { useDispatch } from "react-redux";
import { useSimpleError } from "hooks/apolloWrappers";
import { showError as showErrorWithSnackBar, showInfo } from "actions/actionCreators/snackbarActions";
import { loadPropByCode } from "features/partner/actionCreators";

const PartnerExtraInformationContainer = ({ partnerId, rights, hasWritePermission }) => {
  const [partnerExtraInfos, setPartnerExtraInfos] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState([]);
  const [saving, setSaving] = useState([]);
  const [savedWithMandatoryPropertiesEmpty, setSavedWithMandatoryExtraInfoEmpty] = useState(false);
  const countryCode = useSelector(state => state.params.defaultCountryCode, shallowEqual);
  const showError = useSimpleError();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loadPropertyDetails = useCallback(
    propCode => {
      return loadPropByCode(propCode)
        .then(data => data)
        .catch(error => {
          dispatch(showErrorWithSnackBar(error.message, error.correlationId));
          throw error;
        });
    },
    [dispatch]
  );

  const { loading } = useQueryWithSimpleErrorHandling(PARTNER_EXTRA_INFO_QUERY, {
    variables: { partnerId },
    fetchPolicy: "network-only",
    onCompleted: data => {
      const sections = data.partnerExtraInfos.data.sections.map(section => {
        let values = section.jsonData ? JSON.parse(section.jsonData) : null;
        if (section.display === "Grid") {
          values = values ?? [
            section.columns.reduce((defaultValues, column) => {
              defaultValues[column.propertyName] = null;
              return defaultValues;
            }, {})
          ];
        }
        return { ...section, values };
      });

      const result = {
        data: { ...data.partnerExtraInfos.data, sections: sections },
        partnerId: data.partnerExtraInfos.partnerId
      };
      setPartnerExtraInfos(result);
      setSaveDisabled(sections.map(() => true));
      setSaving(sections.map(() => false));
    }
  });

  const [updatePartnerExtraInfoSection] = useMutation(UPDATE_PARTNER_EXTRA_INFO, {
    onError: err => {
      showError(err);
      setSaveDisabled(prevState => prevState.map(() => false));
      setSaving(prevState => prevState.map(() => false));
    },
    onCompleted: data => {
      const updatedSection = data.updatePartnerExtraInfo.events.partnerExtraInfoUpdated;

      if (updatedSection === null) {
        showError(t("ExtraInfo.CouldNotBeUpdate"));
        return;
      }

      setPartnerExtraInfos(previousState => {
        const { sections } = previousState.data;
        const sectionIndex = sections.findIndex(
          section =>
            section.extraInformationId === updatedSection.extraInformationId ||
            section.name === updatedSection.sectionName
        );

        const updatedSections = [...sections];

        if (sectionIndex !== -1) {
          updatedSections[sectionIndex] = {
            ...updatedSections[sectionIndex],
            extraInformationId: updatedSection.extraInformationId,
            jsonData: updatedSection.jsonData,
            values: JSON.parse(updatedSection.jsonData)
          };
        } else if (updatedSection.jsonData !== null) {
          updatedSections.push({
            extraInformationId: updatedSection.extraInformationId,
            jsonData: updatedSection.jsonData,
            values: JSON.parse(updatedSection.jsonData),
            name: updatedSection.sectionName
          });
        }

        return { ...previousState, data: { sections: updatedSections } };
      });

      setSaving(prevState => prevState.map(() => false));
      dispatch(showInfo(t("ExtraInfo.Updated")));
    }
  });

  const validateExtraInformation = useCallback(
    sections => {
      return sections.some(
        section =>
          section.values &&
          section.values.some(value =>
            section.columns.some(column => {
              if (column.required && !value[column.propertyName]) return true;
              if (column.propertyType === "PHONE_NUMBER") {
                return value[column.propertyName] && !testPhone(String(value[column.propertyName]), countryCode);
              }
              return false;
            })
          )
      );
    },
    [countryCode]
  );

  const handleSavePartnerExtraInfos = useCallback(
    sectionIndex => {
      const section = partnerExtraInfos.data.sections[sectionIndex];
      if (validateExtraInformation([section])) {
        setSavedWithMandatoryExtraInfoEmpty(true);
        return;
      }

      updatePartnerExtraInfoSection({
        variables: {
          input: {
            extraInformationId: section.extraInformationId,
            sectionName: section.name,
            partnerId: partnerExtraInfos.partnerId,
            jsonData: section.values && section.values.length > 0 ? JSON.stringify(section.values) : null
          }
        }
      });
      setSaving(prevState => {
        const newSaving = [...prevState];
        newSaving[sectionIndex] = true;
        return newSaving;
      });
      setSaveDisabled(prevState => {
        const newSaveDisabled = [...prevState];
        newSaveDisabled[sectionIndex] = true;
        return newSaveDisabled;
      });
    },
    [updatePartnerExtraInfoSection, partnerExtraInfos, validateExtraInformation]
  );

  const handlePropertyChange = (index, columnName, newValue, sectionIdx) => {
    setPartnerExtraInfos(prevState => {
      const { data } = prevState;
      const { sections } = data;

      if (sections[sectionIdx]) {
        const { values } = sections[sectionIdx];

        if (values[index]) {
          values[index][columnName] = newValue;
        }
      }
      return { ...prevState };
    });
    setSaveDisabled(prevState => {
      const newSaveDisabled = [...prevState];
      newSaveDisabled[sectionIdx] = false;
      return newSaveDisabled;
    });
  };

  const handleAddExtraInfo = index => {
    setPartnerExtraInfos(prevState => {
      const { data } = prevState;
      const { sections } = data;
      const { columns } = sections[index];

      if (sections[index]) {
        const { values } = sections[index];
        const localValues = values ?? [];
        const newObject = {};
        columns.forEach(column => {
          newObject[column.propertyName] = null;
        });
        localValues.push(newObject);
        sections[index].values = localValues;
      }

      return { ...prevState };
    });
    setSaveDisabled(prevState => {
      const newSaveDisabled = [...prevState];
      newSaveDisabled[index] = false;
      return newSaveDisabled;
    });
  };

  const handleDeleteRow = (rowIndex, sectionIdx) => {
    setPartnerExtraInfos(prevState => {
      const { data } = prevState;
      const { sections } = data;

      if (sections[sectionIdx]) {
        const { values } = sections[sectionIdx];
        values.splice(rowIndex, 1);
      }
      return { ...prevState };
    });
    setSaveDisabled(prevState => {
      const newSaveDisabled = [...prevState];
      newSaveDisabled[sectionIdx] = false;
      return newSaveDisabled;
    });
  };

  return (
    <Grid container>
      <PartnerExtraInformationComponent
        data={partnerExtraInfos.data}
        setSaveDisabled={setSaveDisabled}
        onPropertyChange={handlePropertyChange}
        rights={rights}
        onDeleteRow={handleDeleteRow}
        hasWritePermission={hasWritePermission}
        onAddExtraInfo={handleAddExtraInfo}
        countryCode={countryCode}
        loadPropertyDetails={loadPropertyDetails}
        onSavePartnerExtraInfos={handleSavePartnerExtraInfos}
        saveDisabled={saveDisabled}
        validateExtraInformation={validateExtraInformation}
        savedWithMandatoryPropertiesEmpty={savedWithMandatoryPropertiesEmpty}
        loading={loading}
        partnerId={partnerId}
        t={t}
        saving={saving}
      />
    </Grid>
  );
};

PartnerExtraInformationContainer.propTypes = {
  partnerId: PropTypes.number,
  rights: PropTypes.object.isRequired,
  hasWritePermission: PropTypes.bool.isRequired
};

export default withTranslation()(withRights(PartnerExtraInformationContainer));
